import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../utils/api';

export default function LotTag() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();
  const user = JSON.parse(localStorage.getItem('rz_user') || '{}');
  const dealerName = user.dealer_name || 'Dealership';

  useEffect(() => {
    api.getVehicle(id).then(v => { setVehicle(v); setLoading(false); });
  }, [id]);

  const print = () => window.print();

  if (loading) return <div className="loading">Loading...</div>;
  if (!vehicle) return <div className="loading">Vehicle not found</div>;

  const qrUrl = `${window.location.origin}/vehicles/edit/${id}`;
  const primaryPhoto = vehicle.photos?.find(p => p.is_primary) || vehicle.photos?.[0];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h1>Lot <span>Tag</span></h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-outline" onClick={() => navigate(`/vehicles/edit/${id}`)}>← Back</button>
          <button className="btn-red" onClick={print}>🖨️ Print Tag</button>
        </div>
      </div>

      <p style={{ color: '#888', fontSize: 13, marginBottom: 24 }}>
        Print this tag and place it on the vehicle windshield. Staff can scan the QR code with their phone to instantly add photos from the lot.
      </p>

      {/* Screen preview */}
      <div style={{ maxWidth: 400, margin: '0 auto' }}>
        <div ref={printRef} id="lot-tag" style={{
          background: '#fff', color: '#000', borderRadius: 12, overflow: 'hidden',
          border: '2px solid #cc0000', fontFamily: 'Arial, sans-serif'
        }}>
          {/* Header */}
          <div style={{ background: '#cc0000', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 18, letterSpacing: 1 }}>{dealerName}</div>
              <div style={{ color: '#ffaaaa', fontSize: 10 }}>{user.city}{user.state ? ', '+user.state : ''}</div>
            </div>
            <div style={{ color: '#fff', fontSize: 11, textAlign: 'right' }}>
              <div>Stock #{vehicle.vin?.slice(-6) || 'N/A'}</div>
            </div>
          </div>

          {/* Photo */}
          {primaryPhoto && (
            <img src={primaryPhoto.url} alt="vehicle"
              style={{ width: '100%', height: 200, objectFit: 'cover' }} />
          )}
          {!primaryPhoto && (
            <div style={{ width: '100%', height: 160, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48 }}>🚗</div>
          )}

          {/* Vehicle Info */}
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#111', marginBottom: 4 }}>
              {vehicle.year} {vehicle.make} {vehicle.model}
            </div>
            {vehicle.trim && <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>{vehicle.trim}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '8px 10px' }}>
                <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: .5 }}>Mileage</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>{Number(vehicle.mileage || 0).toLocaleString()} mi</div>
              </div>
              <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '8px 10px' }}>
                <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: .5 }}>Color</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>{vehicle.color_exterior || 'N/A'}</div>
              </div>
              {vehicle.price_display === 'call_for_price' ? (
                <div style={{ background: '#fff0f0', borderRadius: 6, padding: '8px 10px', gridColumn: '1/-1' }}>
                  <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: .5 }}>Price</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#cc0000' }}>Call for Price</div>
                </div>
              ) : vehicle.price_display === 'payment_only' ? (
                <>
                  {vehicle.down_payment && <div style={{ background: '#fff0f0', borderRadius: 6, padding: '8px 10px' }}>
                    <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: .5 }}>Down Payment</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#cc0000' }}>${Number(vehicle.down_payment).toLocaleString()}</div>
                  </div>}
                  {vehicle.weekly_payment && <div style={{ background: '#fff0f0', borderRadius: 6, padding: '8px 10px' }}>
                    <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: .5 }}>Weekly</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#cc0000' }}>${vehicle.weekly_payment}/wk</div>
                  </div>}
                </>
              ) : (
                <div style={{ background: '#fff0f0', borderRadius: 6, padding: '8px 10px', gridColumn: '1/-1' }}>
                  <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: .5 }}>Price</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#cc0000' }}>${Number(vehicle.price || 0).toLocaleString()}</div>
                  {vehicle.down_payment && <div style={{ fontSize: 12, color: '#888' }}>${Number(vehicle.down_payment).toLocaleString()} down · ${vehicle.weekly_payment}/wk</div>}
                </div>
              )}
            </div>

            {/* VIN */}
            <div style={{ fontSize: 10, color: '#aaa', fontFamily: 'monospace', marginBottom: 12 }}>
              VIN: {vehicle.vin || 'N/A'}
            </div>

            {/* QR Code */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, background: '#f9f9f9', borderRadius: 8, padding: 12 }}>
              <QRCodeSVG value={qrUrl} size={80} level="M" />
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#111', marginBottom: 4 }}>📷 Add Photos</div>
                <div style={{ fontSize: 11, color: '#666', lineHeight: 1.4 }}>Scan with your phone to instantly add photos to this vehicle</div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ background: '#111', padding: '8px 16px', textAlign: 'center' }}>
            <div style={{ color: '#888', fontSize: 10 }}>Powered by Red Zone DMS</div>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #lot-tag, #lot-tag * { visibility: visible; }
          #lot-tag { position: fixed; top: 0; left: 0; width: 100%; }
          .page-header, p { display: none; }
        }
      `}</style>
    </div>
  );
}
